import React, { useRef, useEffect, forwardRef } from "react"
import moment from "moment"
import { FiDownload } from "react-icons/fi"

const EventInvoice = forwardRef(({ invoiceData, autoDownload = false }, ref) => {
  console.log(invoiceData,"invoiceData")
  const invoiceRef = useRef(null)
  const { data, payments, attendees_payments, event, event_configuration, tickets, state_is_same } = invoiceData || {}
  const eventDetails = data?.[0] || {}
  const paymentDetails = payments?.[0] || attendees_payments?.[0] || {}

  const parseTicketData = (ticketData) => {
    try {
      return JSON.parse(ticketData || "{}")
    } catch (error) {
      console.error("Error parsing ticket_data:", error)
      return {}
    }
  }

  const parseAddOnsData = (addOnsData) => {
    try {
      return JSON.parse(addOnsData || "[]")
    } catch (error) {
      console.error("Error parsing add_ons_data:", error)
      return []
    }
  }

  const parseFormData = (formData) => {
    try {
      const parsed = JSON.parse(formData || "[]")
      return parsed[0]?.custom_fields || []
    } catch (error) {
      console.error("Error parsing form_data:", error)
      return []
    }
  }

  const ticketData = parseTicketData(eventDetails.ticket_data)
  const addOnsData = parseAddOnsData(eventDetails.add_ons_data)
  const formFields = parseFormData(eventDetails.form_data)

  const firstName = formFields.find((f) => f.name === "first_name" || f.label.toLowerCase().includes("first"))?.value || eventDetails.first_name || "N/A"
  const lastName = formFields.find((f) => f.name === "last_name" || f.label.toLowerCase().includes("last"))?.value || eventDetails.last_name || ""
  const attendeeEmail = formFields.find((f) => f.name === "email_address" || f.label.toLowerCase().includes("email"))?.value || eventDetails.email || "N/A"

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    return moment(dateString).format("DD MMM YYYY")
  }

  const ticketAmount = Number.parseFloat(paymentDetails.total_ticket_amount || 0)
  const taxAmount = Number.parseFloat(paymentDetails.total_tax_amount || 0)
  const addOnsAmount = Number.parseFloat(paymentDetails.total_addons_amount || 0)
  const couponAmount = parseFloat(paymentDetails.coupon_applied_amount || 0)
  const couponPercentage = paymentDetails.coupon_applied_percentage || "0"
  const subtotal = ticketAmount + addOnsAmount
  const discountedSubtotal = subtotal - couponAmount
  const totalAmount = Number.parseFloat(paymentDetails.total_amount || 0)
  const currency = paymentDetails.currency || "INR"

  const getTaxBreakdown = (taxOptions, state_is_same = "yes") => {
    if (!taxOptions || !Array.isArray(taxOptions)) return []

    if (state_is_same === "yes") {
      // For same state: CGST + SGST
      const cgst = taxOptions.find((tax) => tax.tax_name === "CGST")
      const sgst = taxOptions.find((tax) => tax.tax_name === "SGST")
      return [cgst, sgst].filter(Boolean)
    } else {
      // For different state: IGST
      const igst = taxOptions.find((tax) => tax.tax_name === "IGST")
      return igst ? [igst] : []
    }
  }

  const taxBreakdown = getTaxBreakdown(ticketData.tax_options, state_is_same || "yes")
  const totalTaxPercentage = taxBreakdown.reduce((sum, tax) => sum + (tax.tax_percentage || 0), 0)

  const getRegisteredAddOns = () => {
    if (!addOnsData || !Array.isArray(addOnsData)) return []

    return addOnsData.flatMap((addon) => {
      if (addon.registered_variants && Array.isArray(addon.registered_variants)) {
        return addon.registered_variants.map((variant) => ({
          name: `${addon.add_on_name} (${variant.variant_name})`,
          quantity: variant.quantity,
          price: parseFloat(addon.variant_options?.find((v) => v.variant_name === variant.variant_name)?.variant_price || 0),
          tax_type: addon.tax_type,
          tax_options: addon.tax_options,
        }))
      }
      return []
    })
  }

  const registeredAddOns = getRegisteredAddOns()

  const handleDownloadPDF = () => {
    // Create a new window with the invoice content
    const printWindow = window.open("", "_blank")

    if (printWindow) {
      const invoiceContent = document.getElementById("invoice-content")?.innerHTML

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            @page {
              size: A4;
              margin: 0.5in;
            }
            
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              
              body { 
                margin: 0; 
                padding: 0;
                font-size: 12px;
                line-height: 1.3;
              }
              
              .no-print { 
                display: none !important; 
              }
              
              .print-container {
                max-width: 100%;
                margin: 0;
                padding: 0;
              }
              
              .print-header {
                padding: 16px !important;
                margin-bottom: 12px !important;
              }
              
              .print-content {
                padding: 16px !important;
              }
              
              .print-section {
                margin-bottom: 16px !important;
                page-break-inside: avoid;
              }
              
              .print-grid {
                display: grid !important;
                grid-template-columns: 1fr 1fr !important;
                gap: 16px !important;
                margin-bottom: 16px !important;
              }
              
              .print-table {
                font-size: 11px !important;
                margin-bottom: 16px !important;
              }
              
              .print-table th,
              .print-table td {
                padding: 8px 12px !important;
              }
              
              .print-summary {
                width: 300px !important;
                margin-left: auto !important;
                margin-bottom: 16px !important;
              }
              
              .print-payment {
                display: grid !important;
                grid-template-columns: repeat(3, 1fr) !important;
                gap: 12px !important;
              }
              
              .shadow-xl, .shadow-lg, .shadow-sm { 
                box-shadow: none !important; 
              }
              
              .rounded-lg, .rounded-xl {
                border-radius: 4px !important;
              }
              
              /* Ensure page breaks */
              .page-break-before {
                page-break-before: always;
              }
              
              .page-break-after {
                page-break-after: always;
              }
              
              .avoid-break {
                page-break-inside: avoid;
              }
            }
            
            body { 
              font-family: system-ui, -apple-system, sans-serif; 
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${invoiceContent}
          </div>
        </body>
        </html>
      `)

      printWindow.document.close()

      // Wait for content to load, then print
      setTimeout(() => {
        printWindow.print()
        printWindow.close()
      }, 1000)
    }
  }

  // Trigger download if autoDownload is true
  useEffect(() => {
    console.log(invoiceData, "invoiceData :")
    if (autoDownload) {
      console.log("Auto-download triggered")
      handleDownloadPDF()
    }
  }, [autoDownload])

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-4 text-right no-print">
          <button
            onClick={handleDownloadPDF}
            className="inline-flex items-center px-6 py-3 rounded-lg font-medium text-white shadow-lg hover:shadow-xl transition-all duration-200"
            style={{ backgroundColor: "#184778" }}
          >
            <FiDownload className="w-5 h-5 mr-2" />
            Download PDF
          </button>
        </div>
        <div
          id="invoice-content"
          ref={invoiceRef}
          className="bg-white shadow-xl rounded-lg overflow-hidden print-container"
          style={autoDownload ? { position: "absolute", left: "-9999px" } : {}}
        >
          <div
            className="px-8 py-6 print-header avoid-break"
            style={{ background: "linear-gradient(135deg, #184778 0%, #4964aa 100%)" }}
          >
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="bg-white p-2 rounded-lg shadow-md">
                  <img
                    src={event?.event_logo}
                    alt="Event Logo"
                    className="w-16 h-12 object-contain"
                    onError={(e) => {
                      e.target.src = "/amasicon-logo.png"
                    }}
                  />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">{event_configuration?.organization_name || ""}</h1>
                  <p className="text-blue-100 text-xs">
                    {event?.event_type || ""} • {event?.venue_city || ""}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/30">
                  <h2 className="text-2xl font-bold text-white mb-1">RECEIPT</h2>
                  <div className="text-blue-100 text-xs">Payment Confirmation</div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-8 print-content">
            <div className="grid grid-cols-2 gap-6 mb-6 print-grid print-section avoid-break">
              <div>
                <div className="flex items-center mb-3">
                  <div className="w-1 h-5 rounded-full mr-2" style={{ backgroundColor: "#31719c" }}></div>
                  <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: "#184778" }}>
                    Address
                  </h3>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg border-l-4" style={{ borderColor: "#31719c" }}>
                  <div className="text-xs text-black leading-relaxed space-y-1">
                    <div className="text-sm font-bold" style={{ color: "#184778" }}>
                      {event_configuration?.organization_name || "AMASICON 2025"}
                    </div>
                    <div>{event_configuration?.address_line1 || ""} {event_configuration?.address_line2 || ""}</div>
                    <div>{event_configuration?.city || ""}, {event_configuration?.state_name || event_configuration?.state || ""}</div>
                    <div>{event_configuration?.country_name || event_configuration?.country || ""}, {event_configuration?.postcode || ""}</div>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center mb-3">
                  <div className="w-1 h-5 rounded-full mr-2" style={{ backgroundColor: "#4964aa" }}></div>
                  <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: "#184778" }}>
                    Receipt Details
                  </h3>
                </div>
                <div className="space-y-2">
                  <div className="bg-white p-2 rounded border" style={{ borderColor: "#31719c" }}>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium" style={{ color: "#4964aa" }}>
                        Receipt#
                      </span>
                      <span className="text-xs font-bold text-black">
                        {`${event?.event_name || "Event"}-${eventDetails.reg_code || ""}`}
                      </span>
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded border" style={{ borderColor: "#31719c" }}>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium" style={{ color: "#4964aa" }}>
                        Date of Issue
                      </span>
                      <span className="text-xs font-bold text-black">
                        {formatDate(paymentDetails.payment_date || paymentDetails.created_at) || ""}
                      </span>
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded border" style={{ borderColor: "#31719c" }}>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium" style={{ color: "#4964aa" }}>
                        Order ID
                      </span>
                      <span className="text-xs font-bold text-black">{paymentDetails.id || ""}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-6 print-section avoid-break">
              <div className="flex items-center mb-3">
                <div className="w-1 h-5 rounded-full mr-2" style={{ backgroundColor: "#184778" }}></div>
                <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: "#184778" }}>
                  BILLED TO
                </h3>
              </div>
              <div
                className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded border"
                style={{ borderColor: "#31719c" }}
              >
                <div className="space-y-2 text-xs">
                  <div className="flex items-center">
                    <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: "#4964aa" }}></div>
                    <span className="font-medium w-28" style={{ color: "#4964aa" }}>
                      Customer Name
                    </span>
                    <span className="text-black font-bold">{`${firstName} ${lastName}`.trim() || "N/A"}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: "#4964aa" }}></div>
                    <span className="font-medium w-28" style={{ color: "#4964aa" }}>
                      Customer Email
                    </span>
                    <span className="text-black">{attendeeEmail || "N/A"}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-6 print-section avoid-break">
              <div className="flex items-center mb-3">
                <div className="w-1 h-5 rounded-full mr-2" style={{ backgroundColor: "#31719c" }}></div>
                <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: "#184778" }}>
                  Order Details
                </h3>
              </div>
              <div className="rounded border overflow-hidden print-table" style={{ borderColor: "#31719c" }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ background: "linear-gradient(135deg, #184778 0%, #4964aa 100%)" }}>
                      <th className="text-left py-3 px-4 font-bold text-white text-xs">Item</th>
                      <th className="text-center py-3 px-4 font-bold text-white text-xs">QTY</th>
                      <th className="text-right py-3 px-4 font-bold text-white text-xs">Base Price</th>
                      <th className="text-right py-3 px-4 font-bold text-white text-xs">Tax</th>
                      <th className="text-right py-3 px-4 font-bold text-white text-xs">Sub Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Main Ticket */}
                    <tr className="bg-white">
                      <td className="py-3 px-4 text-xs text-black border-b" style={{ borderColor: "#31719c" }}>
                        <div className="flex items-center">
                          <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: "#31719c" }}></div>
                          <div>
                            <div className="font-medium">{ticketData.title || ticketData.ticket_name || "Ticket"}</div>
                            <div className="text-gray-500 text-xs">Tax Type: {ticketData.tax_type || "N/A"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center border-b" style={{ borderColor: "#31719c" }}>
                        <span
                          className="inline-block px-2 py-1 rounded text-xs font-medium text-white"
                          style={{ backgroundColor: "#4964aa" }}
                        >
                          {paymentDetails.ticket_count || 1}
                        </span>
                      </td>
                      <td
                        className="py-3 px-4 text-right text-xs font-medium text-black border-b"
                        style={{ borderColor: "#31719c" }}
                      >
                        {currency} {ticketAmount.toFixed(2)}
                      </td>
                      <td
                        className="py-3 px-4 text-right text-xs font-medium text-black border-b"
                        style={{ borderColor: "#31719c" }}
                      >
                        {currency} {taxAmount.toFixed(2)}
                      </td>
                      <td
                        className="py-3 px-4 text-right text-xs font-bold text-black border-b"
                        style={{ borderColor: "#31719c" }}
                      >
                        {currency} {(ticketAmount + taxAmount).toFixed(2)}
                      </td>
                    </tr>

                    {/* Add-ons */}
                    {registeredAddOns.map((addon, index) => {
                      const addonTaxPercentage = addon.tax_options.reduce((sum, tax) => sum + (tax.tax_percentage || 0), 0)
                      const addonTaxAmount = (addon.price * addon.quantity * addonTaxPercentage) / 100
                      return (
                        <tr key={index} className="bg-white">
                          <td className="py-3 px-4 text-xs text-black border-b" style={{ borderColor: "#31719c" }}>
                            <div className="flex items-center">
                              <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: "#31719c" }}></div>
                              <div>
                                <div className="font-medium">{addon.name}</div>
                                <div className="text-gray-500 text-xs">Tax Type: {addon.tax_type || "N/A"}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center border-b" style={{ borderColor: "#31719c" }}>
                            <span
                              className="inline-block px-2 py-1 rounded text-xs font-medium text-white"
                              style={{ backgroundColor: "#4964aa" }}
                            >
                              {addon.quantity}
                            </span>
                          </td>
                          <td
                            className="py-3 px-4 text-right text-xs font-medium text-black border-b"
                            style={{ borderColor: "#31719c" }}
                          >
                            {currency} {(addon.price * addon.quantity).toFixed(2)}
                          </td>
                          <td
                            className="py-3 px-4 text-right text-xs font-medium text-black border-b"
                            style={{ borderColor: "#31719c" }}
                          >
                            {currency} {addonTaxAmount.toFixed(2)}
                          </td>
                          <td
                            className="py-3 px-4 text-right text-xs font-bold text-black border-b"
                            style={{ borderColor: "#31719c" }}
                          >
                            {currency} {((addon.price * addon.quantity) + addonTaxAmount).toFixed(2)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mb-6 print-section avoid-break">
              <div className="flex justify-end">
                <div className="w-80 print-summary">
                  <div
                    className="bg-gradient-to-br from-gray-50 to-blue-50 rounded border overflow-hidden"
                    style={{ borderColor: "#31719c" }}
                  >
                    <div className="p-4 space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="font-medium text-black">Ticket Amount</span>
                        <span className="font-bold text-black">
                          {currency} {ticketAmount.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="font-medium text-black">Add-ons Amount</span>
                        <span className="font-bold text-black">
                          {currency} {addOnsAmount.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs font-medium text-black">
                        <span>Subtotal</span>
                        <span className="font-bold">
                          {currency} {subtotal.toFixed(2)}
                        </span>
                      </div>
                      {couponAmount > 0 && (
                        <>
                          <div className="flex justify-between text-xs text-red-600">
                            <span className="font-medium">Discount ({couponPercentage}%)</span>
                            <span className="font-bold">-{currency} {couponAmount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="font-medium text-black">Subtotal after discount</span>
                            <span className="font-bold text-black">
                              {currency} {discountedSubtotal.toFixed(2)}
                            </span>
                          </div>
                        </>
                      )}

                      {/* Tax Breakdown */}
                      {taxBreakdown.map((tax, index) => (
                        <div key={index} className="flex justify-between text-xs">
                          <span className="font-medium text-black">
                            {tax.tax_name} @ {tax.tax_percentage}%
                          </span>
                          <span className="font-bold text-black">
                            {currency}{" "}
                            {totalTaxPercentage > 0 ? (taxAmount * (tax.tax_percentage / totalTaxPercentage)).toFixed(2) : "0.00"}
                          </span>
                        </div>
                      ))}

                      {taxBreakdown.length > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="font-medium text-black">
                            Total Tax ({taxBreakdown.map((t) => `${t.tax_name} ${t.tax_percentage}%`).join(" + ")})
                          </span>
                          <span className="font-bold text-black">
                            {currency} {taxAmount.toFixed(2)}
                          </span>
                        </div>
                      )}

                      <div className="border-t-2 pt-2 mt-3" style={{ borderColor: "#31719c" }}>
                        <div
                          className="flex justify-between items-center p-2 rounded"
                          style={{ backgroundColor: "#184778" }}
                        >
                          <span className="font-bold text-white text-sm">Total Amount</span>
                          <span className="font-bold text-white text-lg">
                            {currency} {totalAmount.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-6 print-section avoid-break">
              <div className="flex items-center mb-3">
                <div className="w-1 h-5 rounded-full mr-2" style={{ backgroundColor: "#184778" }}></div>
                <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: "#184778" }}>
                  PAYMENT DETAILS
                </h3>
              </div>
              <div
                className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded border"
                style={{ borderColor: "#31719c" }}
              >
                <div className="grid grid-cols-3 gap-4 print-payment">
                  <div className="text-center">
                    <div className="bg-white p-3 rounded border" style={{ borderColor: "#31719c" }}>
                      <div className="text-xs font-medium mb-1" style={{ color: "#4964aa" }}>
                        Amount Paid
                      </div>
                      <div className="text-sm font-bold" style={{ color: "#184778" }}>
                        {currency}{" "}
                        {Number.parseFloat(paymentDetails.payment_received || totalAmount).toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="bg-white p-3 rounded border" style={{ borderColor: "#31719c" }}>
                      <div className="text-xs font-medium mb-1" style={{ color: "#4964aa" }}>
                        Date Paid
                      </div>
                      <div className="text-sm font-bold" style={{ color: "#184778" }}>
                        {formatDate(paymentDetails.payment_date) || "N/A"}
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="bg-white p-3 rounded border" style={{ borderColor: "#31719c" }}>
                      <div className="text-xs font-medium mb-1" style={{ color: "#4964aa" }}>
                        Payment Method
                      </div>
                      <div className="text-sm font-bold" style={{ color: "#184778" }}>
                        {paymentDetails.payment_id ? "Razorpay" : paymentDetails.payment_method || "N/A"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-center pt-4 border-t" style={{ borderColor: "#31719c" }}>
              <div className="text-xs" style={{ color: "#4964aa" }}>
                <div className="font-medium">Powered by</div>
                <div className="mt-1 text-gray-400">This is a digitally generated receipt</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})

export default EventInvoice